import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import {
  Database,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  AlertTriangle,
  GitBranch,
  Eye,
  CheckCircle2,
  Clock,
  Archive,
  ArrowRight,
  ShieldCheck,
  Layers,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Wand2,
  Tag,
} from 'lucide-react';
import { frameworkVersionsApi } from '@domains/compliance/definition/frameworkVersions/frameworkVersions.api';
import { notify } from '@core/notifications/notify';
import { useModal, ConfirmModal, FrameworkModal, FrameworkVersionsDisplayModal, FrameworkVersionModal, FrameworkAssignmentWizard } from '@components/modal';
import PageInfoButton from '@/components/PageInfoButton';
import type {
  FrameworkVersion,
  FrameworkWithVersions,
  CreateFrameworkDTO,
  UpdateFrameworkDTO,
  CreateFrameworkVersionDTO,
  UpdateFrameworkVersionDTO
} from '@domains/compliance/definition/frameworkVersions/frameworkVersions.dto';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function VersionStatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
        <CheckCircle2 size={12} /> Active
      </span>
    );
  }
  if (status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
        <Clock size={12} /> Draft
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-slate-100 text-slate-600 border border-slate-200">
      <Archive size={12} /> Archived
    </span>
  );
}

function EmptyState({ hasCreate, onCreate, onWizard }: { hasCreate: boolean; onCreate: () => void; onWizard: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-12">
      <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <Database className="text-blue-500" size={40} />
      </div>
      <h3 className="text-xl font-bold text-slate-800 mb-2">No Frameworks Yet</h3>
      <p className="text-slate-500 max-w-md mb-8">
        Start by creating a framework manually or deploy a pre-built one from the library.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={onWizard} className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-sm">
          <Wand2 size={18} /> Quick Assign Wizard
        </button>
        {hasCreate && (
          <button onClick={onCreate} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm">
            <Plus size={18} /> Create Framework
          </button>
        )}
        <button onClick={() => navigate('/compliance/definition/library')} className="inline-flex items-center gap-2 px-5 py-3 bg-white text-blue-700 font-semibold rounded-xl border-2 border-blue-200 hover:bg-blue-50 transition-colors">
          <Sparkles size={18} /> Deploy from Library
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const Frameworks: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const [frameworks, setFrameworks] = useState<FrameworkWithVersions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [outdatedCompanies, setOutdatedCompanies] = useState<any[]>([]);
  const [outdatedLoaded, setOutdatedLoaded] = useState(false);
  const [expandedFrameworks, setExpandedFrameworks] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('fw_expanded');
      return saved ? new Set(JSON.parse(saved) as number[]) : new Set<number>();
    } catch { return new Set<number>(); }
  });
  const [selectedFramework, setSelectedFramework] = useState<FrameworkWithVersions | null>(null);

  useEffect(() => { loadFrameworks(); }, []);

  // Phase 4: Load companies on outdated framework versions for the upgrade banner.
  // See docs/COMPLIANCE_LIFECYCLE_PLAN.md §7 Phase 4.
  useEffect(() => {
    (async () => {
      try {
        const { lifecycleAuthorApi } = await import('@/domains/compliance/lifecycle/VerificationPanel');
        const rows = await lifecycleAuthorApi.outdatedCompanies();
        setOutdatedCompanies(rows);
      } catch {
        // non-blocking
      } finally {
        setOutdatedLoaded(true);
      }
    })();
  }, []);

  const loadFrameworks = async () => {
    setIsLoading(true);
    try {
      const data = await frameworkVersionsApi.getAllFrameworks();
      setFrameworks(data);

      // Check URL for ?expand=ID to auto-expand a specific framework
      const params = new URLSearchParams(window.location.search);
      const expandId = params.get('expand');
      if (expandId) {
        const id = parseInt(expandId, 10);
        if (!isNaN(id)) {
          const expanded = new Set<number>([id]);
          setExpandedFrameworks(expanded);
          localStorage.setItem('fw_expanded', JSON.stringify(Array.from(expanded)));
          return;
        }
      }

      // Only expand the first item on first load
      const firstFramework = data[0];
      if (!localStorage.getItem('fw_expanded') && firstFramework) {
        const expanded = new Set<number>([firstFramework.id]);
        setExpandedFrameworks(expanded);
        localStorage.setItem('fw_expanded', JSON.stringify(Array.from(expanded)));
      }
    } catch (error) {
      notify.error('Failed to load frameworks');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFrameworks = frameworks.filter(framework =>
    framework && (
      (framework.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (framework.authority?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (framework.description?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  const toggleExpand = (id: number) => {
    setExpandedFrameworks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem('fw_expanded', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleCreateFramework = async (frameworkData: any) => {
    if (!frameworkData.name) { notify.error('Framework name is required'); throw new Error('Framework name is required'); }
    try {
      await frameworkVersionsApi.createFramework(frameworkData as CreateFrameworkDTO);
      notify.success('Framework created successfully');
      loadFrameworks();
    } catch (error: any) {
      notify.error(error.message || 'Failed to create framework');
      throw error;
    }
  };

  const handleUpdateFramework = async (frameworkData: any, selectedFramework: FrameworkWithVersions) => {
    if (!frameworkData.name) { notify.error('Framework name is required'); throw new Error('Framework name is required'); }
    try {
      await frameworkVersionsApi.updateFramework(selectedFramework.id, frameworkData as UpdateFrameworkDTO);
      notify.success('Framework updated successfully');
      loadFrameworks();
    } catch (error: any) {
      notify.error(error.message || 'Failed to update framework');
      throw error;
    }
  };

  const handleCreateVersion = async (versionData: any) => {
    if (!versionData.framework_id || !versionData.version) { notify.error('Framework and version are required'); throw new Error('Required'); }
    try {
      const newVersion = await frameworkVersionsApi.createFrameworkVersion(versionData as CreateFrameworkVersionDTO);
      notify.success('Framework version created successfully');
      if (selectedFramework) {
        setSelectedFramework({ ...selectedFramework, versions: [...selectedFramework.versions, newVersion] });
      }
      loadFrameworks();
    } catch (error: any) {
      notify.error(error.message || 'Failed to create version');
      throw error;
    }
  };

  const handleUpdateVersion = async (versionData: any, selectedVersion: FrameworkVersion) => {
    if (!versionData.version) { notify.error('Version is required'); throw new Error('Required'); }
    try {
      if (versionData.status === 'active') {
        await frameworkVersionsApi.activateFrameworkVersion(selectedVersion.id);
        notify.success('Version activated — now available for mapping');
      } else {
        const updateData = { ...versionData }; delete updateData.status;
        await frameworkVersionsApi.updateFrameworkVersion(selectedVersion.id, updateData as UpdateFrameworkVersionDTO);
        notify.success('Version updated');
      }
      if (selectedFramework) {
        const updatedVersions = selectedFramework.versions.map(v => v.id === selectedVersion.id ? { ...v, ...versionData } : v);
        setSelectedFramework({ ...selectedFramework, versions: updatedVersions });
      }
      loadFrameworks();
    } catch (error: any) {
      notify.error(error.message || 'Failed to update version');
      throw error;
    }
  };

  const handleActivateVersion = async (versionId: number) => {
    try {
      await frameworkVersionsApi.activateFrameworkVersion(versionId);
      notify.success('Version activated — ready for mapping');
      loadFrameworks();
    } catch (error: any) {
      if (error.code === 'ACTIVATION_IMPACT' && error.impact) {
        const { currentActiveVersionName, affectedAssignments } = error.impact;
        const companyNames = affectedAssignments.map((a: any) => a.companyName).join(', ');
        openModal({
          component: ConfirmModal,
          props: {
            title: 'Version Has Active Assignments',
            message: `Activating this version will archive version "${currentActiveVersionName}" which currently has ${affectedAssignments.length} active company assignment(s): ${companyNames}.\n\nThese companies will remain on the old version until migrated. Do you want to proceed?`,
            confirmText: 'Activate Anyway',
            cancelText: 'Cancel',
            type: 'danger',
            onConfirm: async () => {
              try {
                await frameworkVersionsApi.activateFrameworkVersion(versionId, true);
                notify.success('Version activated — ready for mapping');
                loadFrameworks();
              } catch (err: any) {
                notify.error(err.message || 'Failed to activate version');
              }
            },
            onCancel: () => {},
          },
        });
      } else {
        notify.error(error.message || 'Failed to activate version');
      }
    }
  };

  // ─── Modal openers ─────────────────────────────────────────────────────

  const openCreateFrameworkModal = () => {
    openModal({ component: FrameworkModal, props: { onSubmit: handleCreateFramework, selectedFramework: null }, options: { size: 'large' } });
  };

  const openEditFramework = (framework: FrameworkWithVersions) => {
    openModal({ component: FrameworkModal, props: { onSubmit: handleUpdateFramework, selectedFramework: framework }, options: { size: 'large' } });
  };

  const openCreateVersion = (framework: FrameworkWithVersions) => {
    setSelectedFramework(framework);
    openModal({ component: FrameworkVersionModal, props: { onSubmit: handleCreateVersion, selectedVersion: null, frameworkId: framework.id }, options: { size: 'large' } });
  };

  const openEditVersion = (version: FrameworkVersion, framework: FrameworkWithVersions) => {
    setSelectedFramework(framework);
    openModal({ component: FrameworkVersionModal, props: { onSubmit: handleUpdateVersion, selectedVersion: version, frameworkId: version.framework_id }, options: { size: 'large' } });
  };

  // Phase 3: Clone an active version → creates draft v(n+1) + cloned template + carries clause_refs.
  // See docs/COMPLIANCE_LIFECYCLE_PLAN.md §7 Phase 3.
  const handleCloneVersion = async (sourceVersion: any, framework: FrameworkWithVersions) => {
    const newVersion = window.prompt(
      `New version number for "${framework.name}" (cloning from v${sourceVersion.version}):`,
      `${sourceVersion.version}.1`,
    );
    if (!newVersion || newVersion.trim() === '') return;
    try {
      const { lifecycleAuthorApi } = await import('@/domains/compliance/lifecycle/VerificationPanel');
      const result = await lifecycleAuthorApi.cloneVersion(sourceVersion.id, newVersion.trim());
      if (!result.ok) {
        notify.error(result.error ?? 'Clone failed');
        return;
      }
      notify.success(`Cloned ${result.data.clonedNodeCount} nodes into draft v${newVersion}. Opening builder…`);
      navigate(
        `/compliance/definition/builder?name=${encodeURIComponent(framework.name)}&frameworkId=${framework.id}&versionId=${result.data.newVersionId}&templateId=${result.data.newTemplateId}&previousVersionId=${sourceVersion.id}`,
      );
    } catch (e: any) {
      notify.error(e?.message ?? 'Clone failed');
    }
  };

  const openDeleteFramework = (framework: FrameworkWithVersions) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Framework',
        message: `Are you sure you want to delete "${framework.name}"? This removes all versions and cannot be undone.`,
        confirmText: 'Delete Framework', cancelText: 'Cancel', type: 'danger',
        onConfirm: async () => {
          try { await frameworkVersionsApi.deleteFramework(framework.id); notify.success('Framework deleted'); loadFrameworks(); }
          catch (e: any) { notify.error(e.message || 'Failed to delete'); }
        },
        onCancel: () => {},
      },
    });
  };

  // ─── Derived data ──────────────────────────────────────────────────────

  const totalVersions = frameworks.reduce((acc, f) => acc + (f.versions?.length || 0), 0);
  const readyForMapping = frameworks.filter(f => f.versions?.some((v: any) => v.status === 'active')).length;
  const draftsCount = frameworks.filter(f => f.versions?.length === 0 || f.versions?.every((v: any) => v.status !== 'active')).length;

  // ─── Loading / Permission states ───────────────────────────────────────

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (!hasPermission('frameworks.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <AlertTriangle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You don't have permission to access Framework Management.</p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-slate-900 text-left">Compliance Frameworks</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Create frameworks, add versions, then open the Builder on a draft version to build and publish</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => openModal({ component: FrameworkAssignmentWizard, props: { onComplete: () => {} }, options: { size: 'large' } })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              <Wand2 size={16} /> Quick Assign
            </button>
            <button onClick={() => navigate('/compliance/definition/library')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
              <Sparkles size={16} className="text-violet-500" /> Deploy from Library
            </button>
            {hasPermission('frameworks.create') && (
              <button onClick={openCreateFrameworkModal} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                <Plus size={16} /> Create Framework
              </button>
            )}
          </div>
        </div>

        {/* Phase 4 — Companies on outdated framework versions */}
        {outdatedLoaded && outdatedCompanies.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-amber-900 mb-1">
                  ⚠️ {outdatedCompanies.length} compan{outdatedCompanies.length === 1 ? 'y is' : 'ies are'} on outdated framework versions
                </p>
                <p className="text-xs text-amber-700 mb-2">
                  These companies have an active assignment that points at a non-active framework version. Migrate them so they work against the latest standards.
                </p>
                <ul className="text-xs text-amber-800 space-y-1 max-h-40 overflow-y-auto">
                  {outdatedCompanies.slice(0, 8).map((row: any) => (
                    <li key={`${row.assignment_id}-${row.company_id}`} className="flex items-center gap-2">
                      <span className="font-semibold">{row.company_name}</span>
                      <span className="text-amber-600">on</span>
                      <span className="font-mono">{row.framework_name} v{row.current_version}</span>
                      <span className="text-amber-600">→ active is</span>
                      <span className="font-mono font-bold">v{row.active_version}</span>
                      <span className="text-amber-500">({row.pages_certified}/{row.pages_total} certified)</span>
                    </li>
                  ))}
                  {outdatedCompanies.length > 8 && (
                    <li className="text-amber-600 italic">…and {outdatedCompanies.length - 8} more</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Frameworks</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{frameworks.length}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Versions</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{totalVersions}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 bg-emerald-50/50">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider text-left">Ready for Mapping</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{readyForMapping}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Need Active Version</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{draftsCount}</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, authority, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
            />
          </div>
        </div>

        {/* Framework List — nested collapsible */}
        {filteredFrameworks.length === 0 ? (
          <EmptyState hasCreate={hasPermission('frameworks.create')} onCreate={openCreateFrameworkModal} onWizard={() => openModal({ component: FrameworkAssignmentWizard, props: { onComplete: () => {} }, options: { size: 'large' } })} />
        ) : (
          <div className="space-y-2">
            {filteredFrameworks.map(framework => {
              const isExpanded = expandedFrameworks.has(framework.id);
              const activeVersion = framework.versions?.find((v: any) => v.status === 'active') as any;
              const hasActive = !!activeVersion;
              const versionCount = framework.versions?.length || 0;

              return (
                <div key={framework.id} className="bg-white rounded-3xl border border-slate-100 overflow-hidden">

                  {/* Framework parent row */}
                  <div
                    className={`flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors ${hasActive ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-slate-200'}`}
                    onClick={() => toggleExpand(framework.id)}
                  >
                    {/* Expand icon */}
                    <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />

                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${hasActive ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <Database size={18} className={hasActive ? 'text-emerald-600' : 'text-slate-400'} />
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate text-left">{framework.name}</p>
                      <p className="text-xs text-slate-500 truncate text-left">
                        {framework.creator_name ? `by ${framework.creator_name}` : ''}{framework.creator_name && framework.authority ? ' · ' : ''}{framework.authority || ''}
                      </p>
                    </div>

                    {/* Status badge */}
                    {hasActive ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800">
                        <CheckCircle2 size={12} /> v{activeVersion.version} Active
                      </span>
                    ) : versionCount > 0 ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-[10px] bg-slate-100 text-slate-600">
                        <Clock size={12} /> {versionCount} draft{versionCount > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-[10px] bg-slate-100 text-slate-500">
                        No versions
                      </span>
                    )}

                    {/* Version count */}
                    <span className="text-xs text-slate-400 flex-shrink-0">{versionCount} version{versionCount !== 1 ? 's' : ''}</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{new Date(framework.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>

                    {/* Actions — hidden when expanded (shown inside panel instead) */}
                    {!isExpanded && (
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {hasPermission('frameworks.create') && (
                          <button onClick={() => openCreateVersion(framework)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors">
                            <Plus size={13} /> Add Version
                          </button>
                        )}
                        {hasPermission('frameworks.update') && (
                          <button onClick={() => openEditFramework(framework)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Edit"><Edit size={15} /></button>
                        )}
                        {/* Phase 3 safeguard: hide Delete when framework has assignments or certificates.
                            Backend also rejects deletion server-side — UI just avoids the confusing red button.
                            Admin can still force-delete via the (deferred) Admin Escape-Hatch Console. */}
                        {hasPermission('frameworks.delete') && ((framework as any).assignment_count ?? 0) === 0 && ((framework as any).certification_count ?? 0) === 0 && (
                          <button onClick={() => openDeleteFramework(framework)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><Trash2 size={15} /></button>
                        )}
                        {hasPermission('frameworks.delete') && (((framework as any).assignment_count ?? 0) > 0 || ((framework as any).certification_count ?? 0) > 0) && (
                          <span
                            className="p-1.5 text-slate-300 cursor-not-allowed"
                            title={`Cannot delete — ${(framework as any).assignment_count ?? 0} assignment(s), ${(framework as any).certification_count ?? 0} certificate(s) reference this framework. Unassign and revoke first.`}
                          >
                            <Trash2 size={15} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Nested versions — indented with left margin */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50">
                      {/* Info cards */}
                      <div className="ml-14 mr-5 my-3 grid grid-cols-3 gap-3">
                        {/* Info card: Authority */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Tag size={12} /> Authority
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{framework.authority || '—'}</p>
                        </div>

                        {/* Info card: Versions */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Layers size={12} /> Versions
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{versionCount} version{versionCount !== 1 ? 's' : ''}</p>
                        </div>

                        {/* Info card: Created */}
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <Clock size={12} /> Created
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{new Date(framework.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{new Date(framework.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{framework.creator_name ? ` by ${framework.creator_name}` : ''}</p>
                        </div>
                      </div>

                      {versionCount === 0 ? (
                        <div className="ml-14 mr-5 my-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600 flex items-center gap-2">
                          <AlertTriangle size={16} className="flex-shrink-0 text-slate-400" />
                          <span>No versions yet. Click <strong>Add Version</strong> above and set status to <strong>Active</strong> to enable template mapping.</span>
                        </div>
                      ) : (
                        <div className="py-2">
                          {framework.versions.map((v: any) => {
                            const isActive = v.status === 'active';
                            const isDraft = v.status === 'draft';
                            const isArchived = !isActive && !isDraft;

                            return (
                              <div
                                key={v.id}
                                className={`ml-14 mr-5 mb-1.5 flex items-center justify-between rounded-lg px-4 py-2.5 text-sm transition-colors ${
                                  isActive ? 'bg-emerald-50 border border-emerald-200' :
                                  isDraft ? 'bg-slate-50 border border-slate-200' :
                                  'bg-slate-50 border border-slate-200'
                                }`}
                              >
                                {/* Left: icon + version + badge */}
                                <div className="flex items-center gap-2.5">
                                  {isActive && <CheckCircle2 size={16} className="text-emerald-600" />}
                                  {isDraft && <Clock size={16} className="text-slate-400" />}
                                  {isArchived && <Archive size={16} className="text-slate-400" />}

                                  <span className={`font-bold ${isActive ? 'text-emerald-800' : isDraft ? 'text-slate-700' : 'text-slate-500'}`}>
                                    v{v.version}
                                  </span>

                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                    isActive ? 'bg-emerald-200 text-emerald-800' :
                                    isDraft ? 'bg-slate-200 text-slate-700' :
                                    'bg-slate-200 text-slate-600'
                                  }`}>
                                    {v.status}
                                  </span>

                                  {isActive && (
                                    <span className="text-xs text-emerald-600">— Ready for assignment</span>
                                  )}
                                  {isDraft && !v.draft_template_id && (
                                    <span className="text-xs text-slate-500">— Click Open Builder to start building</span>
                                  )}
                                  {isDraft && v.draft_template_id && (
                                    <span className="text-xs text-indigo-600">— Work in progress, click Open Builder to resume</span>
                                  )}
                                </div>

                                {/* Right: actions */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {/*
                                    Phase 3: naked "Activate" is only shown when the version has framework_mappings.
                                    This prevents the broken "active-but-no-mappings" half-state. For a brand-new
                                    draft, the only path to activation is the builder's "Publish & Activate" CTA
                                    (which atomically publishes template + writes mappings + flips status).
                                  */}
                                  {isDraft && (v.mapping_count ?? 0) > 0 && (
                                    <button
                                      onClick={() => handleActivateVersion(v.id)}
                                      className="px-3 py-1 text-xs font-bold text-white bg-slate-600 hover:bg-slate-700 rounded-lg transition-colors"
                                      title="Re-activate this previously-mapped version"
                                    >
                                      Activate
                                    </button>
                                  )}
                                  {isDraft && (
                                    <button
                                      onClick={() => {
                                        const url = `/compliance/definition/builder?name=${encodeURIComponent(framework.name)}&frameworkId=${framework.id}&versionId=${v.id}` +
                                          (v.draft_template_id ? `&templateId=${v.draft_template_id}` : '');
                                        navigate(url);
                                      }}
                                      className="px-2.5 py-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                                      title={v.draft_template_id
                                        ? `Resume building template #${v.draft_template_id}`
                                        : 'Open the builder with this framework + version context preloaded'}
                                    >
                                      {v.draft_template_id ? 'Resume Builder' : 'Open Builder'}
                                    </button>
                                  )}
                                  {isActive && (
                                    <button
                                      onClick={() => openModal({
                                        component: FrameworkAssignmentWizard,
                                        props: {
                                          preselectedFrameworkId: framework.id,
                                          preselectedVersionId: v.id,
                                          onComplete: () => { loadFrameworks(); notify.success('Framework assigned. Compliance pages generated for the company.'); },
                                        },
                                        options: { size: 'large' },
                                      })}
                                      className="px-3 py-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                                      title={`Assign ${framework.name} v${v.version} to a company`}
                                    >
                                      👥 Assign to Company
                                    </button>
                                  )}
                                  {isActive && v.draft_template_id && (
                                    <button
                                      onClick={() => navigate(`/compliance/definition/builder?name=${encodeURIComponent(framework.name)}&frameworkId=${framework.id}&versionId=${v.id}&templateId=${v.draft_template_id}`)}
                                      className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
                                      title="View the published template in the builder (read-only)"
                                    >
                                      View Builder
                                    </button>
                                  )}
                                  {isActive && (
                                    <button
                                      onClick={() => handleCloneVersion(v, framework)}
                                      className="px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors"
                                      title="Create a new draft version pre-populated with this version's template + clause refs"
                                    >
                                      Clone Version
                                    </button>
                                  )}
                                  {!isActive && (
                                    <button
                                      onClick={() => openEditVersion(v, framework)}
                                      className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                    >
                                      Edit
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                        </div>
                      )}

                      {/* Framework actions — shown inside panel when expanded */}
                      <div className="ml-14 mr-5 mt-2 mb-3 flex items-center gap-2">
                        {hasPermission('frameworks.create') && (
                          <button onClick={() => openCreateVersion(framework)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors">
                            <Plus size={13} /> Add Version
                          </button>
                        )}
                        {hasPermission('frameworks.update') && (
                          <button onClick={() => openEditFramework(framework)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors">
                            <Edit size={13} /> Edit Framework
                          </button>
                        )}
                        {/* Phase 3 safeguard: same rule as collapsed header — hide Delete when framework has dependencies */}
                        {hasPermission('frameworks.delete') && ((framework as any).assignment_count ?? 0) === 0 && ((framework as any).certification_count ?? 0) === 0 && (
                          <button onClick={() => openDeleteFramework(framework)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors">
                            <Trash2 size={13} /> Delete
                          </button>
                        )}
                        {hasPermission('frameworks.delete') && (((framework as any).assignment_count ?? 0) > 0 || ((framework as any).certification_count ?? 0) > 0) && (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-400 bg-slate-50 rounded-lg border border-slate-200 cursor-not-allowed"
                            title={`Cannot delete — ${(framework as any).assignment_count ?? 0} assignment(s), ${(framework as any).certification_count ?? 0} certificate(s). Unassign and revoke first.`}
                          >
                            <Trash2 size={13} /> Delete locked
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Frameworks;
